import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MenuDish, SimpleMenuDish } from 'src/app/shared/Models/menudish';

/**
 * Tree structure change event for coordinating virtual scroll updates
 * Components get viewport nodes directly from NgRx selectors
 */
export interface TreeStructureChange {
  updateList: boolean;
  afterDnD: boolean;
  resetScroll?: boolean;
}

/**
 * Dishes change event
 */
export interface DishesChangeEvent {
  dishes: (SimpleMenuDish | MenuDish)[];
  sendRequest: boolean;
  updateScroll: boolean;
}

/**
 * Service responsible for coordinating tree state changes and emitting
 * observable events for the virtual scroll component.
 *
 * Provides coordination signals without maintaining shadow state.
 * All tree data should be accessed from NgRx store via selectors.
 */
@Injectable({
  providedIn: 'root',
})
export class TreeStateService {
  // Observable streams for tree state changes
  readonly refreshList$ = new Subject<void>();
  readonly treeStructureChanged$ = new BehaviorSubject<TreeStructureChange>({
    updateList: false,
    afterDnD: false,
  });
  readonly itemsCleared$ = new Subject<void>();

  /**
   * Clear stored items and emit event for external listeners
   */
  clearStoredItems(): void {
    this.clearStoredItemsInternal();
    // Emit event that items have been cleared for external listeners
    this.itemsCleared$.next();
  }

  /**
   * Clear stored items internally without emitting the cleared event
   * Used by tree component to avoid recursion
   */
  clearStoredItemsInternal(): void {
    this.refreshList$.next();
    this.updateVirtualScroll(false, false);
  }

  /**
   * Update the virtual scroll viewport
   * Emits coordination event without data
   */
  updateVirtualScroll(
    updateList = true,
    afterDnD = false,
    resetScroll = false,
  ): void {
    // Emit coordination event (no data)
    this.treeStructureChanged$.next({
      updateList,
      afterDnD,
      resetScroll,
    });
  }

  /**
   * Emit refresh list signal
   */
  emitRefreshList(): void {
    this.refreshList$.next();
  }
}
